import { env } from '@config/env';

/**
 * OIDC authorization-code flow — request shape for the `/signin/oauth` redirect.
 *
 * Restores the legacy portal's sign-in INITIATION (legacy
 * `src/app/pages/Auth/Oauth/index.js`) that the portalshadcn migration dropped:
 * the migration pointed both `/signin/oauth` and `/signin/oauth/redirect` at the
 * callback page, so `/signin/oauth` never sent the browser to Keycloak.
 *
 * Exported as a pure builder — like `authRequestBuilders` in ./api.ts — so the
 * exact authorize URL is wire-shape-locked in oidc.test.ts.
 */
export const OIDC_CLIENT_ID = 'user-api';
export const OIDC_SCOPES = 'email openid profile';

/**
 * Build the Keycloak authorization-endpoint URL for the auth-code flow.
 *
 * The `redirect_uri` origin prefers `VITE_PORTAL_URL` (the legacy
 * `Config.Portal.Url` / `$PORTAL_FQDN`), which CI sets per-env to the STABLE
 * Keycloak-whitelisted host (`https://portal.<env>.ordermesh.io`). This is what
 * keeps OAuth working on ephemeral hosts — PR preview SWA envs build with the
 * development value, so the redirect_uri is `portal.dev.ordermesh.io`, not the
 * un-whitelisted `*.azurestaticapps.net` origin. When `VITE_PORTAL_URL` is unset
 * (local dev) it falls back to `fallbackOrigin` (the browser origin). Either way
 * the resulting `redirect_uri` MUST be registered on the Keycloak `user-api`
 * client, or Keycloak rejects the flow with "Invalid parameter: redirect_uri".
 *
 * Encoding matches the legacy initiation exactly (`encodeURIComponent`, i.e. `%20`
 * for spaces) since that flow was known-good in production.
 */
export function buildOauthAuthorizeUrl(fallbackOrigin: string): string {
  const redirectOrigin = (env.VITE_PORTAL_URL ?? fallbackOrigin).replace(/\/+$/, '');
  const authority = `https://${env.VITE_AUTH_HOST}/realms/${env.authRealm}/protocol/openid-connect`;
  const redirectUri = encodeURIComponent(`${redirectOrigin}/signin/oauth/redirect`);
  const scope = encodeURIComponent(OIDC_SCOPES);
  return `${authority}/auth?client_id=${OIDC_CLIENT_ID}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code`;
}
